import { Button, ButtonProps, InputAdornment, Menu, MenuItem, SxProps } from "@mui/material";
import React, { PropsWithChildren, createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useDevice } from "./DeviceProvider";
import { get, set } from "lodash-es";
import RestoreIcon from "@mui/icons-material/Restore";
import { useEditedColor } from "../hooks/useEditedStyles";

interface ResetContextMenu {
  openContextMenu: (
    event: React.MouseEvent<HTMLElement, MouseEvent>,
    path: string,
    overrides?: { onReset?: (input: HTMLInputElement) => void; isEdited?: () => boolean }
  ) => void;
  closeContextMenu: () => void;
}

const ResetMenuContext = createContext<ResetContextMenu | undefined>(undefined);

function findInputTarget(element: Element): HTMLInputElement | null {
  if (["INPUT", "TEXTAREA"].includes(element.tagName)) {
    return element as HTMLInputElement;
  }
  let input: HTMLInputElement | null = (Array.from(element.children).find((item) => ["INPUT", "TEXTAREA"].includes(item.tagName)) as HTMLInputElement) ?? null;
  if (input) {
    return input;
  }
  if (element.children) {
    Array.from(element.children).some((item) => {
      input = findInputTarget(item);
      return input;
    });
    if (input) {
      return input;
    }
  }
  return null;
}

function findTargetElement(element: Element): HTMLElement | null {
  if (element.classList.contains("restoreContextMenuTarget")) {
    return element as HTMLElement;
  }
  let target: HTMLElement | null = (Array.from(element.children).find((item) => item.classList.contains("restoreContextMenuTarget")) as HTMLElement) ?? null;
  if (target) {
    return target;
  }
  if (element.children) {
    Array.from(element.children).some((item) => {
      target = findTargetElement(item);
      return target;
    });
    if (target) {
      return target;
    }
  }
  return null;
}

export const ResetContextMenuProvider = ({ children }: PropsWithChildren<{}>) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const resetValueRef = useRef<{
    path: string;
    saved: string | boolean | null;
    input: HTMLInputElement | null;
    currentInputValue: string | boolean | null;
    originalTarget: EventTarget & HTMLElement;
    onReset?: (input: HTMLInputElement) => void;
  } | null>(null);

  const { formatMessage } = useIntl();
  const { device, setCurrentValue } = useDevice();

  const { currentValue, config } = device ?? {};

  const openContextMenu = useCallback(
    (event: React.MouseEvent<HTMLElement, MouseEvent>, path: string, overrides?: { onReset?: (input: HTMLInputElement) => void; isEdited?: () => boolean }) => {
      // console.log("CONTEXT MENU", path);
      const { toolboxConfig } = config ?? {};
      if (!toolboxConfig) {
        return;
      }

      // console.log("find input:", event.currentTarget);
      const input = findInputTarget(event.currentTarget);
      const target = input ? null : findTargetElement(event.currentTarget);
      if (!input && !target) {
        return;
      }

      // if (overrides?.isEdited && !overrides.isEdited()) {
      //   return;
      // }

      // // console.log("type:", input.type, input.value, saved, JSON.stringify([input.value, saved]));
      // switch (input.type) {
      //   case "text":
      //   case "textarea":
      //   case "range":
      //     if (saved === input.value || `${saved}` === input.value) {
      //       return;
      //     }
      //     break;
      //   case "checkbox":
      //     if (saved === input.checked) {
      //       return;
      //     }
      //     break;
      // }

      event.preventDefault();
      event.stopPropagation();

      const currentInputValue = get(currentValue, path);
      const saved = get(toolboxConfig, path);

      setAnchorEl(event.currentTarget);
      resetValueRef.current = { path, saved, input, currentInputValue, originalTarget: event.currentTarget, onReset: overrides?.onReset };
    },
    [config, currentValue]
  );

  const closeContextMenu = useCallback(() => {
    setAnchorEl(null);
    resetValueRef.current = null;
  }, []);

  const resetValue = () => {
    if (!resetValueRef.current) {
      return;
    }
    const { currentInputValue, input, path, saved } = resetValueRef.current;
    if (input) {
      if (resetValueRef.current.onReset) {
        resetValueRef.current.onReset(input);
        closeContextMenu();
        return;
      }
      switch (input.type) {
        case "text":
        case "textarea":
          if (currentInputValue === input.value) {
            input.value = saved as string;
          }
          break;
        case "checkbox":
          if (currentInputValue === input.checked) {
            input.checked = saved as boolean;
          }
          break;
      }
    }

    try {
      set(currentValue, path, saved);
      setCurrentValue(currentValue);
    } catch (err) {
      console.log(err);
    }
    closeContextMenu();
  };

  const value = useMemo(
    () => ({
      openContextMenu,
      closeContextMenu,
    }),
    [openContextMenu, closeContextMenu]
  );

  return (
    <ResetMenuContext.Provider value={value}>
      {children}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={closeContextMenu}
        MenuListProps={{
          "aria-labelledby": "lock-button",
          role: "listbox",
        }}
      >
        <MenuItem selected={false} onClick={resetValue}>
          {formatMessage({ defaultMessage: "Reset to saved value" })}
        </MenuItem>
      </Menu>
    </ResetMenuContext.Provider>
  );
};

export const useResetContextMenu = () => {
  const resetContextMenu = useContext(ResetMenuContext);
  if (resetContextMenu === undefined) {
    throw new Error("useResetContextMenu must be used within an ResetContextMenuProvider");
  }
  return resetContextMenu;
};

export const ResetAdornment = ({ sx }: { sx?: SxProps }) => {
  return (
    <InputAdornment position="end">
      <RestoreIcon fontSize="inherit" sx={sx} />
    </InputAdornment>
  );
};

export const RestoreButton = ({ path, saved, sx, ...props }: Omit<ButtonProps, "children"> & { path: string; saved: any | null }) => {
  const { text: editedText } = useEditedColor();
  const { formatMessage } = useIntl();
  const {
    device: { currentValue },
    setCurrentValue,
  } = useDevice();

  const resetAll = useCallback(() => {
    set(currentValue, path, saved);
    setCurrentValue(currentValue);
  }, [currentValue, path, saved, setCurrentValue]);

  return (
    <Button startIcon={<RestoreIcon fontSize="inherit" />} sx={{ color: editedText, ...sx }} onClick={resetAll} {...props}>
      {formatMessage({ defaultMessage: "Restore all" })}
    </Button>
  );
};
